// Catalog Shell - Pane UI Components
// Reusable UI components for building panes
// ============================================================================

import { PaneTypeRenderContext } from '../core/types';
import { Shell } from '../core/shell';
import { createElement } from './dom';
import { debounce } from './dom';

// ============================================================================
// Filter Bar
// A toggle button group for filtering/switching pane content modes
// ============================================================================

export interface FilterOption<T extends string = string> {
    /** Unique value for this option */
    value: T;
    /** Display label for the button */
    label: string;
}

export interface FilterBarConfig<T extends string = string> {
    /** Available filter options */
    options: FilterOption<T>[];
    /** Key in pane context where the current value is stored */
    contextKey: string;
    /** Default value if not set in context */
    defaultValue: T;
    /** Called when the filter value changes */
    onChange: (value: T) => void;
}

export interface FilterBar<T extends string = string> {
    /** The filter bar element to add to the DOM */
    element: HTMLElement;
    /** Get the current filter value */
    getValue: () => T;
    /** Programmatically set the filter value (updates UI and calls onChange) */
    setValue: (value: T) => void;
    /** Enable/disable a single option's button (e.g. block a switch that isn't valid yet). */
    setOptionDisabled: (value: T, disabled: boolean) => void;
    /** Clean up event listeners */
    destroy: () => void;
}

/**
 * Creates a filter bar with toggle buttons that persists its state.
 *
 * @example
 * ```typescript
 * const filterBar = createFilterBar(ctx, {
 *     options: [
 *         { value: 'basic', label: 'Basic' },
 *         { value: 'all', label: 'All' },
 *     ],
 *     contextKey: 'settingsMode',
 *     defaultValue: 'basic',
 *     onChange: (value) => renderContent(value),
 * });
 *
 * header.appendChild(filterBar.element);
 * ```
 */
export function createFilterBar<T extends string = string>(
    ctx: PaneTypeRenderContext,
    config: FilterBarConfig<T>
): FilterBar<T> {
    const { options, contextKey, defaultValue, onChange } = config;

    // Get current value from context, or use default
    let currentValue: T = (ctx.instance.context[contextKey] as T) ?? defaultValue;

    // Create container
    const container = createElement('div', {
        className: 'cs-filter-bar',
    });

    // Track buttons for updating active state
    const buttons: Map<T, HTMLButtonElement> = new Map();

    // Update button active states
    const updateButtonStates = (value: T) => {
        for (const [btnValue, btn] of buttons) {
            if (btnValue === value) {
                btn.classList.add('cs-active');
            } else {
                btn.classList.remove('cs-active');
            }
        }
    };

    // Create buttons
    for (const option of options) {
        const btn = createButton({
            label: option.label,
            variant: 'state',
            active: option.value === currentValue,
            onClick: () => {
                if (currentValue !== option.value) {
                    currentValue = option.value;
                    ctx.instance.context[contextKey] = currentValue;
                    updateButtonStates(currentValue);
                    onChange(currentValue);
                    // Persist state
                    (ctx.shell as Shell).saveConfiguration();
                }
            },
        });

        buttons.set(option.value, btn);
        container.appendChild(btn);
    }

    return {
        element: container,
        getValue: () => currentValue,
        setValue: (value: T) => {
            if (currentValue !== value && buttons.has(value)) {
                currentValue = value;
                ctx.instance.context[contextKey] = currentValue;
                updateButtonStates(currentValue);
                onChange(currentValue);
                (ctx.shell as Shell).saveConfiguration();
            }
        },
        setOptionDisabled: (value: T, disabled: boolean) => {
            const btn = buttons.get(value);
            if (btn) btn.disabled = disabled;
        },
        destroy: () => {
            // Buttons are removed when container is removed from DOM
            buttons.clear();
        },
    };
}

// ============================================================================
// Slider
// Range input with optional value display (formatted text label or
// editable number input). Use for toolbar-grade controls; the
// entity-view form renderer wraps this for form fields.
// ============================================================================

/**
 * Value-display configuration for `createSlider`.
 *  - `none`:  no companion element next to the slider.
 *  - `text`:  read-only formatted label; optional click handler (e.g. reset).
 *  - `input`: editable number input synced bidirectionally to the slider.
 */
export type SliderValueDisplay =
    | { kind: 'none' }
    | {
        kind: 'text';
        /** Format the numeric value into the displayed string (default: String(v)). */
        format?: (value: number) => string;
        /** Click handler on the label (e.g. "click to reset to 100%"). */
        onClick?: () => void;
        /** Reserve a minimum width so the label doesn't shift as digits change. */
        minWidth?: number | string;
    }
    | { kind: 'input' };

export interface SliderConfig {
    min: number;
    max: number;
    step?: number;
    /** Initial value (defaults to `min`). */
    value?: number;
    disabled?: boolean;
    /** Companion element to the right of the range. Default: none. */
    valueDisplay?: SliderValueDisplay;
    /** Width applied to the range input (number = px, string = CSS length). */
    width?: number | string;
    title?: string;
    /** Fires continuously while the user drags or types. */
    onInput?: (value: number) => void;
    /** Fires on commit (mouseup, blur, Enter). */
    onChange?: (value: number) => void;
}

export interface Slider {
    element: HTMLElement;
    rangeInput: HTMLInputElement;
    getValue: () => number;
    /**
     * Set the slider value programmatically. No-op while the user is
     * actively dragging — guards against late state echoes (e.g.
     * server-side updates) overwriting the drag position.
     */
    setValue: (value: number) => void;
    setDisabled: (disabled: boolean) => void;
    /** Detach event listeners. The container is freed when removed from the DOM. */
    destroy: () => void;
}

/**
 * Build a shell-styled slider. Wraps an `<input type="range">` and an
 * optional value display (label or editable number input). All
 * interaction routes through `onInput` / `onChange`, with dragging
 * state suppression baked in so external `setValue` calls during a
 * drag are ignored.
 *
 * Example — toolbar zoom slider with a clickable percentage label:
 * ```typescript
 * const zoom = createSlider({
 *   min: 0.05, max: 4, step: 0.01,
 *   value: 1, width: 240,
 *   valueDisplay: {
 *     kind: 'text',
 *     format: v => `${Math.round(v * 100)}%`,
 *     onClick: () => zoom.setValue(1),
 *     minWidth: 40,
 *   },
 *   onInput: v => applyZoom(v),
 * });
 * toolbar.appendChild(zoom.element);
 * ```
 */
export function createSlider(config: SliderConfig): Slider {
    const {
        min, max,
        step = 1,
        value = min,
        disabled = false,
        valueDisplay = { kind: 'none' },
        width,
        title,
        onInput,
        onChange,
    } = config;

    const container = createElement('div', { className: 'cs-slider' });

    const range = createElement('input', {
        className: 'cs-slider-range',
        attributes: {
            type: 'range',
            min: String(min),
            max: String(max),
            step: String(step),
            value: String(value),
            ...(disabled ? { disabled: '' } : {}),
            ...(title ? { title } : {}),
        },
    }) as HTMLInputElement;
    if (width !== undefined) {
        range.style.width = typeof width === 'number' ? `${width}px` : width;
    }
    container.appendChild(range);

    let dragging = false;
    const onDragStart = () => { dragging = true; };
    const onDragEnd = () => { dragging = false; };
    range.addEventListener('pointerdown', onDragStart);
    range.addEventListener('pointerup', onDragEnd);
    range.addEventListener('pointercancel', onDragEnd);

    let textEl: HTMLElement | null = null;
    let numberEl: HTMLInputElement | null = null;
    const fmt = valueDisplay.kind === 'text' && valueDisplay.format
        ? valueDisplay.format
        : (v: number) => String(v);

    if (valueDisplay.kind === 'text') {
        textEl = createElement('span', {
            className: 'cs-slider-value-text',
            textContent: fmt(value),
        });
        if (valueDisplay.minWidth !== undefined) {
            textEl.style.minWidth = typeof valueDisplay.minWidth === 'number'
                ? `${valueDisplay.minWidth}px`
                : valueDisplay.minWidth;
        }
        if (valueDisplay.onClick) {
            const onClick = valueDisplay.onClick;
            textEl.addEventListener('click', onClick);
            textEl.style.cursor = 'pointer';
        }
        container.appendChild(textEl);
    } else if (valueDisplay.kind === 'input') {
        numberEl = createElement('input', {
            className: 'cs-slider-value-input',
            attributes: {
                type: 'number',
                min: String(min),
                max: String(max),
                step: String(step),
                value: String(value),
                ...(disabled ? { disabled: '' } : {}),
            },
        }) as HTMLInputElement;
        container.appendChild(numberEl);
    }

    const syncDisplay = (v: number): void => {
        if (textEl) textEl.textContent = fmt(v);
        else if (numberEl) numberEl.value = String(v);
    };

    const handleRangeInput = () => {
        const v = Number(range.value);
        syncDisplay(v);
        onInput?.(v);
    };
    const handleRangeChange = () => onChange?.(Number(range.value));
    range.addEventListener('input', handleRangeInput);
    range.addEventListener('change', handleRangeChange);

    const handleNumberChange = numberEl
        ? () => {
            let v = Number(numberEl!.value);
            if (!Number.isFinite(v)) v = min;
            v = Math.max(min, Math.min(max, v));
            numberEl!.value = String(v);
            range.value = String(v);
            onInput?.(v);
            onChange?.(v);
        }
        : null;
    if (numberEl && handleNumberChange) {
        numberEl.addEventListener('change', handleNumberChange);
    }

    return {
        element: container,
        rangeInput: range,
        getValue: () => Number(range.value),
        setValue: (v: number) => {
            if (dragging) return;
            range.value = String(v);
            syncDisplay(v);
        },
        setDisabled: (d: boolean) => {
            range.disabled = d;
            if (numberEl) numberEl.disabled = d;
        },
        destroy: () => {
            range.removeEventListener('pointerdown', onDragStart);
            range.removeEventListener('pointerup', onDragEnd);
            range.removeEventListener('pointercancel', onDragEnd);
            range.removeEventListener('input', handleRangeInput);
            range.removeEventListener('change', handleRangeChange);
            if (numberEl && handleNumberChange) {
                numberEl.removeEventListener('change', handleNumberChange);
            }
        },
    };
}

// ============================================================================
// Pane Header
// A standardized header with title and optional action area
// ============================================================================

export interface PaneHeaderConfig {
    /** Title text */
    title: string;
    /** Optional subtitle (e.g., camera ID) */
    subtitle?: string;
    /** Optional action elements (filter bars, buttons, etc.) */
    actions?: HTMLElement[];
}

/**
 * Creates a standardized pane header with title and optional actions.
 *
 * @example
 * ```typescript
 * const header = createPaneHeader({
 *     title: 'Settings',
 *     subtitle: cameraId,
 *     actions: [filterBar.element],
 * });
 * container.appendChild(header);
 * ```
 */
export function createPaneHeader(config: PaneHeaderConfig): HTMLElement {
    const { title, subtitle, actions } = config;

    const header = createElement('div', {
        className: 'cs-pane-header',
    });

    // Title section
    const titleSection = createElement('div', {
        className: 'cs-pane-header-title',
    });

    const titleText = subtitle ? `${title} (${subtitle})` : title;
    const titleEl = createElement('h3', {
        textContent: titleText,
    });
    titleSection.appendChild(titleEl);
    header.appendChild(titleSection);

    // Actions section (if any)
    if (actions && actions.length > 0) {
        const actionsSection = createElement('div', {
            className: 'cs-pane-header-actions',
        });
        for (const action of actions) {
            actionsSection.appendChild(action);
        }
        header.appendChild(actionsSection);
    }

    return header;
}

// ============================================================================
// Scrollable Content Area
// A container that scrolls and fills available space
// ============================================================================

/**
 * Creates a scrollable content container.
 * Use this for the main content area of a pane.
 */
export function createScrollableContent(className?: string): HTMLElement {
    return createElement('div', {
        className: `cs-pane-content ${className ?? ''}`.trim(),
    });
}

/**
 * Creates the standard root container for a pane's render() function.
 * Provides full-size scrollable content area with padding.
 *
 * @param className Optional extra class(es) to add
 *
 * @example
 * ```typescript
 * render: (ctx) => {
 *     const container = createPaneView();
 *     container.appendChild(myContent);
 *     return container;
 * }
 * ```
 */
export function createPaneView(className?: string): HTMLElement {
    return createElement('div', {
        className: `pane-view ${className ?? ''}`.trim(),
    });
}

// ============================================================================
// Button
// A styled button with variant and size options
// ============================================================================

export type ButtonVariant = 'default' | 'yes' | 'no' | 'save' | 'state';
export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonConfig {
    /** Button label text */
    label?: string;
    /** Visual variant (default: 'default') */
    variant?: ButtonVariant;
    /** Size variant (default: 'medium') */
    size?: ButtonSize;
    /** Optional icon (prepended to label). String for emoji/text, Element for SVG. */
    icon?: string | Element;
    /** Click handler */
    onClick?: (e: MouseEvent) => void;
    /** Whether button is disabled */
    disabled?: boolean;
    /** Optional title/tooltip */
    title?: string;
    /** Additional CSS class */
    className?: string;
    /** Whether the button starts in active/on state (state variant only) */
    active?: boolean;
    /**
     * Retint the button by setting the --cs-btn-h / -s / -l HSL inputs
     * inline. The base cs-btn rule derives its bevel, hover, and active
     * colors from these three values via calc() so only the inputs need
     * to be provided — no per-shade overrides. Values are CSS strings
     * (e.g. `h: '200deg'`, `s: '65%'`, `l: '18%'`).
     */
    backgroundHsl?: { h: string; s: string; l: string };
}
export function createButton(config: ButtonConfig): HTMLButtonElement {
    const {
        label = '',
        variant = 'default',
        size = 'medium',
        icon,
        onClick,
        disabled = false,
        title,
        className,
        active = false,
        backgroundHsl,
    } = config;

    // Build class list
    const classes = ['cs-btn'];

    if (variant !== 'default') {
        classes.push(`cs-btn--${variant}`);
    }

    if (size === 'small') {
        classes.push('cs-btn--small');
    } else if (size === 'large') {
        classes.push('cs-btn--large');
    }

    // Icon-only button (no label)
    if (icon && !label) {
        classes.push('cs-btn--icon');
    } else if (icon) {
        classes.push('cs-btn--icon-text');
    }

    if (className) {
        classes.push(className);
    }

    const btn = createElement('button', {
        className: classes.join(' '),
        attributes: {
            type: 'button',
            ...(title ? { title } : {}),
        },
    });

    // Add icon if provided
    if (icon) {
        const iconSpan = createElement('span', { className: 'cs-btn-icon' });
        if (typeof icon === 'string') {
            iconSpan.textContent = icon;
        } else {
            iconSpan.appendChild(icon);
        }
        btn.appendChild(iconSpan);
    }

    // Add label if provided. Wrapped in a span so consumers can hide
    // the label via CSS (e.g. a compact icon-only mode) without
    // rebuilding the button.
    if (label) {
        const labelSpan = createElement('span', { className: 'cs-btn-label' });
        labelSpan.textContent = label;
        btn.appendChild(labelSpan);
    }

    if (disabled) {
        btn.disabled = true;
    }

    if (backgroundHsl) {
        btn.style.setProperty('--cs-btn-h', backgroundHsl.h);
        btn.style.setProperty('--cs-btn-s', backgroundHsl.s);
        btn.style.setProperty('--cs-btn-l', backgroundHsl.l);
    }

    if (variant === 'state') {
        if (active) {
            btn.classList.add('cs-active');
        }
        if (onClick) {
            // Caller manages active state
            btn.addEventListener('click', onClick);
        } else {
            // Auto-toggle when no onClick provided
            btn.addEventListener('click', () => {
                btn.classList.toggle('cs-active');
            });
        }
    } else if (onClick) {
        btn.addEventListener('click', onClick);
    }

    return btn;
}

// ============================================================================
// Toolbar
// A horizontal container for action buttons
// ============================================================================

export interface ToolbarConfig {
    /** Child elements (buttons, etc.) */
    children: HTMLElement[];
    /** Additional CSS class */
    className?: string;
}

/**
 * Creates a toolbar container for action buttons.
 *
 * @example
 * ```typescript
 * const toolbar = createToolbar({
 *     children: [
 *         createButton({ label: 'New', variant: 'primary', onClick: handleNew }),
 *         createButton({ label: 'Delete', variant: 'danger', onClick: handleDelete }),
 *     ],
 * });
 * container.appendChild(toolbar);
 * ```
 */
export function createToolbar(config: ToolbarConfig): HTMLElement {
    const { children, className } = config;

    const toolbar = createElement('div', {
        className: `cs-pane-toolbar ${className ?? ''}`.trim(),
    });

    for (const child of children) {
        toolbar.appendChild(child);
    }

    return toolbar;
}

// ============================================================================
// Empty State
// A placeholder for empty lists/content areas
// ============================================================================

export interface EmptyStateConfig {
    /** Icon to display (emoji or text) */
    icon?: string;
    /** Main message */
    message: string;
    /** Optional secondary description */
    description?: string;
    /** Optional action button */
    action?: {
        label: string;
        onClick: () => void;
    };
}

/**
 * Creates an empty state placeholder.
 *
 * @example
 * ```typescript
 * const empty = createEmptyState({
 *     icon: '📁',
 *     message: 'No files found',
 *     description: 'Upload a file to get started',
 *     action: {
 *         label: 'Upload',
 *         onClick: () => showUploadDialog(),
 *     },
 * });
 * container.appendChild(empty);
 * ```
 */
export function createEmptyState(config: EmptyStateConfig): HTMLElement {
    const { icon, message, description, action } = config;

    const container = createElement('div', {
        className: 'cs-empty-state',
    });

    if (icon) {
        const iconEl = createElement('div', {
            className: 'cs-empty-state-icon',
            textContent: icon,
        });
        container.appendChild(iconEl);
    }

    const messageEl = createElement('div', {
        className: 'cs-empty-state-message',
        textContent: message,
    });
    container.appendChild(messageEl);

    if (description) {
        const descEl = createElement('div', {
            className: 'cs-empty-state-description',
            textContent: description,
        });
        container.appendChild(descEl);
    }

    if (action) {
        const actionBtn = createButton({
            label: action.label,
            variant: 'yes',
            onClick: action.onClick,
        });
        container.appendChild(actionBtn);
    }

    return container;
}

// ============================================================================
// Error State
// A display for error conditions
// ============================================================================

export interface ErrorStateConfig {
    /** Error message */
    message: string;
    /** Optional retry callback */
    onRetry?: () => void;
}

/**
 * Creates an error state display.
 *
 * @example
 * ```typescript
 * const error = createErrorState({
 *     message: 'Failed to load sessions',
 *     onRetry: () => dataSource.refresh(),
 * });
 * container.appendChild(error);
 * ```
 */
export function createErrorState(config: ErrorStateConfig): HTMLElement {
    const { message, onRetry } = config;

    const container = createElement('div', {
        className: 'cs-error-state',
    });

    const iconEl = createElement('div', {
        className: 'cs-error-state-icon',
        textContent: '!',
    });
    container.appendChild(iconEl);

    const messageEl = createElement('div', {
        className: 'cs-error-state-message',
        textContent: message,
    });
    container.appendChild(messageEl);

    if (onRetry) {
        const retryBtn = createButton({
            label: 'Retry',
            onClick: onRetry,
        });
        container.appendChild(retryBtn);
    }

    return container;
}

// ============================================================================
// Loading State
// A loading indicator
// ============================================================================

export interface LoadingStateConfig {
    /** Optional loading message (default: 'Loading...') */
    message?: string;
}

/**
 * Creates a loading state indicator.
 *
 * @example
 * ```typescript
 * const loading = createLoadingState({ message: 'Loading sessions...' });
 * container.appendChild(loading);
 * ```
 */
export function createLoadingState(config: LoadingStateConfig = {}): HTMLElement {
    const { message = 'Loading...' } = config;

    const container = createElement('div', {
        className: 'cs-loading-state',
    });

    const spinner = createElement('div', {
        className: 'cs-loading-spinner',
    });
    container.appendChild(spinner);

    const messageEl = createElement('div', {
        className: 'cs-loading-message',
        textContent: message,
    });
    container.appendChild(messageEl);

    return container;
}

// ============================================================================
// Badge
// A small label for status/category indication
// ============================================================================

export type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'muted';

export interface BadgeConfig {
    /** Badge text */
    text: string;
    /** Visual variant (default: 'default') */
    variant?: BadgeVariant;
    /** Additional CSS class */
    className?: string;
}

/**
 * Creates a badge element.
 *
 * @example
 * ```typescript
 * const statusBadge = createBadge({
 *     text: 'Recording',
 *     variant: 'danger',
 * });
 *
 * const typeBadge = createBadge({
 *     text: 'Monitor',
 *     variant: 'info',
 * });
 * ```
 */
export function createBadge(config: BadgeConfig): HTMLElement {
    const { text, variant = 'default', className } = config;

    const classes = ['cs-badge'];

    if (variant !== 'default') {
        classes.push(`cs-badge--${variant}`);
    }

    if (className) {
        classes.push(className);
    }

    return createElement('span', {
        className: classes.join(' '),
        textContent: text,
    });
}

// ============================================================================
// List Pane Container
// Standard structure for list-based panes
// ============================================================================

export interface ListPaneContainerConfig {
    /** Optional header element above filter bar and toolbar */
    header?: HTMLElement;
    /** Optional filter bar element */
    filterBar?: HTMLElement;
    /** Optional toolbar element */
    toolbar?: HTMLElement;
    /** Additional CSS class for container */
    className?: string;
}

export interface ListPaneContainer {
    /** The container element */
    element: HTMLElement;
    /** The body element where DataListView should be mounted */
    body: HTMLElement;
}

/**
 * Creates the standard container structure for list panes.
 * Returns both the container and the body element for mounting DataListView.
 *
 * @example
 * ```typescript
 * const { element, body } = createListPaneContainer({
 *     filterBar: filterBar.element,
 *     toolbar: createToolbar({
 *         children: [createButton({ label: 'New', variant: 'primary', onClick: handleNew })],
 *     }),
 * });
 *
 * const dataListView = new DataListView({
 *     container: body,  // Mount in body, not element
 *     ...config,
 * });
 *
 * return element;
 * ```
 */
export function createListPaneContainer(config: ListPaneContainerConfig = {}): ListPaneContainer {
    const { header, filterBar, toolbar, className } = config;

    const container = createElement('div', {
        className: `cs-list-pane ${className ?? ''}`.trim(),
    });

    // Add header if provided (above filter bar and toolbar)
    if (header) {
        container.appendChild(header);
    }

    // Add filter bar if provided
    if (filterBar) {
        container.appendChild(filterBar);
    }

    // Add toolbar if provided
    if (toolbar) {
        container.appendChild(toolbar);
    }

    // Create body for DataListView
    const body = createElement('div', {
        className: 'cs-list-pane__body',
    });
    container.appendChild(body);

    return { element: container, body };
}

// ============================================================================
// Filter Controls
// A composable filter system with multiple filter types
// ============================================================================

/**
 * Base configuration for all filter types.
 */
interface BaseFilterDef {
    /** Unique key for this filter in the filters object */
    key: string;
    /** Optional label (shown for some filter types) */
    label?: string;
}

/**
 * Toggle button group filter (like the existing filter bar).
 */
export interface ToggleFilterDef extends BaseFilterDef {
    type: 'toggle';
    options: Array<{ value: string; label: string }>;
    defaultValue: string;
}

/**
 * Dropdown select filter.
 */
export interface SelectFilterDef extends BaseFilterDef {
    type: 'select';
    options: Array<{ value: string; label: string }>;
    defaultValue: string;
    placeholder?: string;
}

/**
 * Text input filter (with optional debounce).
 */
export interface TextFilterDef extends BaseFilterDef {
    type: 'text';
    defaultValue: string;
    placeholder?: string;
    /** Debounce delay in ms (default: 300) */
    debounceMs?: number;
}

/**
 * Checkbox filter.
 */
export interface CheckboxFilterDef extends BaseFilterDef {
    type: 'checkbox';
    defaultValue: boolean;
    /** Label shown next to checkbox */
    checkboxLabel: string;
}

export interface DateFilterDef extends BaseFilterDef {
    type: 'date';
    defaultValue: string;
    placeholder?: string;
}

/**
 * Union of all filter definition types.
 */
export type FilterDef = ToggleFilterDef | SelectFilterDef | TextFilterDef | CheckboxFilterDef | DateFilterDef;

/**
 * Configuration for filter controls.
 */
export interface FilterControlsConfig {
    /** Array of filter definitions */
    filters: FilterDef[];
    /**
     * Key prefix for storing filter values in pane context.
     * If provided, filters are persisted across pane re-renders.
     */
    contextKey?: string;
    /** Called when any filter value changes */
    onChange: (filters: Record<string, unknown>) => void;
}

/**
 * Filter controls instance returned by createFilterControls.
 */
export interface FilterControls {
    /** The container element with all filter controls */
    element: HTMLElement;
    /** Get all current filter values */
    getFilters: () => Record<string, unknown>;
    /** Get a specific filter value */
    getFilter: <T = unknown>(key: string) => T | undefined;
    /** Set a specific filter value programmatically */
    setFilter: (key: string, value: unknown) => void;
    /** Reset all filters to their default values */
    reset: () => void;
    /** Clean up event listeners */
    destroy: () => void;
}

/**
 * Creates a composable filter controls component.
 *
 * Supports multiple filter types that work together:
 * - toggle: Toggle button group (like status filters)
 * - select: Dropdown select
 * - text: Text input with debounce (for search)
 * - checkbox: Single checkbox toggle
 *
 * @example
 * ```typescript
 * // Simple single filter (replaces createFilterBar)
 * const filters = createFilterControls(ctx, {
 *     filters: [
 *         {
 *             key: 'status',
 *             type: 'toggle',
 *             options: [
 *                 { value: 'all', label: 'All' },
 *                 { value: 'active', label: 'Active' },
 *             ],
 *             defaultValue: 'all',
 *         },
 *     ],
 *     contextKey: 'myListFilters',
 *     onChange: (filters) => dataSource.setFilters(filters),
 * });
 *
 * // Multiple filters
 * const filters = createFilterControls(ctx, {
 *     filters: [
 *         {
 *             key: 'status',
 *             type: 'toggle',
 *             options: [...],
 *             defaultValue: 'all',
 *         },
 *         {
 *             key: 'search',
 *             type: 'text',
 *             placeholder: 'Search...',
 *             defaultValue: '',
 *             debounceMs: 300,
 *         },
 *         {
 *             key: 'category',
 *             type: 'select',
 *             options: [...],
 *             defaultValue: '',
 *         },
 *         {
 *             key: 'showArchived',
 *             type: 'checkbox',
 *             checkboxLabel: 'Show archived',
 *             defaultValue: false,
 *         },
 *     ],
 *     onChange: (filters) => dataSource.setFilters(filters),
 * });
 * ```
 */
export function createFilterControls(
    ctx: PaneTypeRenderContext,
    config: FilterControlsConfig
): FilterControls {
    const { filters: filterDefs, contextKey, onChange } = config;

    // Initialize filter values from context or defaults
    const filterValues: Record<string, unknown> = {};
    for (const def of filterDefs) {
        const storedKey = contextKey ? `${contextKey}.${def.key}` : null;
        const storedValue = storedKey ? ctx.instance.context[storedKey] : undefined;
        filterValues[def.key] = storedValue ?? def.defaultValue;
    }

    // Container for all controls
    const container = createElement('div', {
        className: 'cs-filter-controls',
    });

    // Track elements for updates
    const controlElements = new Map<string, HTMLElement>();
    const cleanupFns: Array<() => void> = [];

    // Helper to trigger onChange and persist
    const triggerChange = () => {
        // Persist to context if contextKey provided
        if (contextKey) {
            for (const [key, value] of Object.entries(filterValues)) {
                ctx.instance.context[`${contextKey}.${key}`] = value;
            }
            (ctx.shell as Shell).saveConfiguration();
        }
        onChange({ ...filterValues });
    };

    // Render each filter
    for (const def of filterDefs) {
        let element: HTMLElement;

        switch (def.type) {
            case 'toggle':
                element = renderToggleFilter(def, filterValues, triggerChange, cleanupFns);
                break;
            case 'select':
                element = renderSelectFilter(def, filterValues, triggerChange, cleanupFns);
                break;
            case 'text':
                element = renderTextFilter(def, filterValues, triggerChange, cleanupFns);
                break;
            case 'checkbox':
                element = renderCheckboxFilter(def, filterValues, triggerChange, cleanupFns);
                break;
            case 'date':
                element = renderDateFilter(def, filterValues, triggerChange, cleanupFns);
                break;
        }

        controlElements.set(def.key, element);
        container.appendChild(element);
    }

    return {
        element: container,
        getFilters: () => ({ ...filterValues }),
        getFilter: <T = unknown>(key: string) => filterValues[key] as T | undefined,
        setFilter: (key: string, value: unknown) => {
            if (key in filterValues) {
                filterValues[key] = value;
                // Update UI - find the control and update it
                updateControlUI(key, value, controlElements, filterDefs);
                triggerChange();
            }
        },
        reset: () => {
            for (const def of filterDefs) {
                filterValues[def.key] = def.defaultValue;
                updateControlUI(def.key, def.defaultValue, controlElements, filterDefs);
            }
            triggerChange();
        },
        destroy: () => {
            for (const cleanup of cleanupFns) {
                cleanup();
            }
            cleanupFns.length = 0;
            controlElements.clear();
        },
    };
}

// ============================================================================
// Filter Renderers (internal)
// ============================================================================

function renderToggleFilter(
    def: ToggleFilterDef,
    values: Record<string, unknown>,
    onChange: () => void,
    cleanupFns: Array<() => void>
): HTMLElement {
    const wrapper = createElement('div', {
        className: 'cs-filter-control cs-filter-control--toggle',
    });

    if (def.label) {
        const label = createElement('label', {
            className: 'cs-filter-label',
            textContent: def.label,
        });
        wrapper.appendChild(label);
    }

    const buttonGroup = createElement('div', {
        className: 'cs-filter-bar',
    });

    const buttons = new Map<string, HTMLButtonElement>();

    for (const option of def.options) {
        const isActive = values[def.key] === option.value;
        const btn = createButton({
            label: option.label,
            variant: 'state',
            active: isActive,
            onClick: () => {
                if (values[def.key] !== option.value) {
                    values[def.key] = option.value;
                    // Update all button states
                    for (const [val, b] of buttons) {
                        b.classList.toggle('cs-active', val === option.value);
                    }
                    onChange();
                }
            },
        });

        buttons.set(option.value, btn);
        buttonGroup.appendChild(btn);
    }

    wrapper.appendChild(buttonGroup);
    wrapper.dataset.filterKey = def.key;
    wrapper.dataset.filterType = 'toggle';

    return wrapper;
}

function renderSelectFilter(
    def: SelectFilterDef,
    values: Record<string, unknown>,
    onChange: () => void,
    cleanupFns: Array<() => void>
): HTMLElement {
    const wrapper = createElement('div', {
        className: 'cs-filter-control cs-filter-control--select',
    });

    if (def.label) {
        const label = createElement('label', {
            className: 'cs-filter-label',
            textContent: def.label,
        });
        wrapper.appendChild(label);
    }

    const select = document.createElement('select');
    select.className = 'cs-filter-select';

    for (const option of def.options) {
        const opt = document.createElement('option');
        opt.value = option.value;
        opt.textContent = option.label;
        opt.selected = values[def.key] === option.value;
        select.appendChild(opt);
    }

    const handler = () => {
        values[def.key] = select.value;
        onChange();
    };

    select.addEventListener('change', handler);
    cleanupFns.push(() => select.removeEventListener('change', handler));

    wrapper.appendChild(select);
    wrapper.dataset.filterKey = def.key;
    wrapper.dataset.filterType = 'select';

    return wrapper;
}

function renderTextFilter(
    def: TextFilterDef,
    values: Record<string, unknown>,
    onChange: () => void,
    cleanupFns: Array<() => void>
): HTMLElement {
    const wrapper = createElement('div', {
        className: 'cs-filter-control cs-filter-control--text',
    });

    if (def.label) {
        const label = createElement('label', {
            className: 'cs-filter-label',
            textContent: def.label,
        });
        wrapper.appendChild(label);
    }

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'cs-filter-input';
    input.placeholder = def.placeholder ?? '';
    input.value = String(values[def.key] ?? '');

    // Debounced handler
    const debounceMs = def.debounceMs ?? 300;
    const debouncedOnChange = debounce(() => {
        values[def.key] = input.value;
        onChange();
    }, debounceMs);

    const handler = () => debouncedOnChange();

    input.addEventListener('input', handler);
    cleanupFns.push(() => input.removeEventListener('input', handler));

    wrapper.appendChild(input);
    wrapper.dataset.filterKey = def.key;
    wrapper.dataset.filterType = 'text';

    return wrapper;
}

function renderCheckboxFilter(
    def: CheckboxFilterDef,
    values: Record<string, unknown>,
    onChange: () => void,
    cleanupFns: Array<() => void>
): HTMLElement {
    const wrapper = createElement('div', {
        className: 'cs-filter-control cs-filter-control--checkbox',
    });

    const label = createElement('label', {
        className: 'cs-filter-checkbox-label',
    });

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'cs-filter-checkbox';
    checkbox.checked = Boolean(values[def.key]);

    const handler = () => {
        values[def.key] = checkbox.checked;
        onChange();
    };

    checkbox.addEventListener('change', handler);
    cleanupFns.push(() => checkbox.removeEventListener('change', handler));

    label.appendChild(checkbox);
    label.appendChild(document.createTextNode(def.checkboxLabel));

    wrapper.appendChild(label);
    wrapper.dataset.filterKey = def.key;
    wrapper.dataset.filterType = 'checkbox';

    return wrapper;
}

function renderDateFilter(
    def: DateFilterDef,
    values: Record<string, unknown>,
    onChange: () => void,
    cleanupFns: Array<() => void>
): HTMLElement {
    const wrapper = createElement('div', {
        className: 'cs-filter-control cs-filter-control--date',
    });

    if (def.label) {
        const label = createElement('label', {
            className: 'cs-filter-label',
            textContent: def.label,
        });
        wrapper.appendChild(label);
    }

    const input = document.createElement('input');
    input.type = 'date';
    input.className = 'cs-filter-date';
    if (def.placeholder) input.placeholder = def.placeholder;
    input.value = String(values[def.key] ?? '');

    const handler = () => {
        values[def.key] = input.value;
        onChange();
    };

    input.addEventListener('change', handler);
    cleanupFns.push(() => input.removeEventListener('change', handler));

    wrapper.appendChild(input);
    wrapper.dataset.filterKey = def.key;
    wrapper.dataset.filterType = 'date';

    return wrapper;
}

function updateControlUI(
    key: string,
    value: unknown,
    controlElements: Map<string, HTMLElement>,
    filterDefs: FilterDef[]
): void {
    const element = controlElements.get(key);
    if (!element) return;

    const def = filterDefs.find(d => d.key === key);
    if (!def) return;

    switch (def.type) {
        case 'toggle': {
            const buttons = element.querySelectorAll('.cs-btn--state');
            buttons.forEach(btn => {
                const btnEl = btn as HTMLButtonElement;
                // Find option value from button text
                const option = def.options.find(o => o.label === btnEl.textContent);
                btnEl.classList.toggle('cs-active', option?.value === value);
            });
            break;
        }
        case 'select': {
            const select = element.querySelector('select') as HTMLSelectElement;
            if (select) select.value = String(value);
            break;
        }
        case 'text': {
            const input = element.querySelector('input') as HTMLInputElement;
            if (input) input.value = String(value);
            break;
        }
        case 'checkbox': {
            const checkbox = element.querySelector('input[type="checkbox"]') as HTMLInputElement;
            if (checkbox) checkbox.checked = Boolean(value);
            break;
        }
        case 'date': {
            const dateInput = element.querySelector('input[type="date"]') as HTMLInputElement;
            if (dateInput) dateInput.value = String(value ?? '');
            break;
        }
    }
}
